import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import type { DealVersionChange } from '@platform/shared-types';

/**
 * PUBLISHING A DEAL VERSION — the moment a deal becomes the thing merchants
 * sign against.
 *
 * TWO BUGS FROM THE DOCUMENT PublishModal ARE DELIBERATELY NOT COPIED HERE:
 *
 * 1. It owns `changeType` in internal state with no callback, so the blast
 *    radius cannot re-fetch when the radio changes — a future-dated publish
 *    then shows a radius evaluated at *now*. Here `changeType` is a PROP with
 *    an `onChangeType` callback, so the caller re-counts on every change.
 *
 * 2. It hard-codes "0 parties affected" for a minor publish, which is false.
 *    Deal staleness is ABSOLUTE: somebody already stale from an earlier
 *    material version stays stale through a minor one. The count is labelled
 *    as what it is rather than zeroed.
 */
export interface DealPublishModalProps {
  /** No default, ever: the publisher chooses, and `undefined` blocks confirm. */
  changeType: 'material' | 'minor' | undefined;
  onChangeType: (next: 'material' | 'minor') => void;
  /** How many signed parties this leaves stale. `null` = not counted yet. */
  affectedCount: number | null;
  changes: DealVersionChange[];
  effectiveAt?: string;
  onEffectiveAtChange?: (iso: string) => void;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const IMPACT_CHIP: Record<DealVersionChange['impact'], string> = {
  money: 'bg-rose-100 text-rose-700 border-rose-200',
  legal: 'bg-amber-100 text-amber-800 border-amber-200',
  presentation: 'bg-stone-100 text-stone-600 border-stone-200',
};

export const DealPublishModal: React.FC<DealPublishModalProps> = ({
  changeType, onChangeType, affectedCount, changes, effectiveAt, onEffectiveAtChange,
  busy, error, onConfirm, onCancel,
}) => {
  // BOTH conditions block. An unknown blast radius is not "probably none", and
  // a missing changeType is not "probably minor".
  const blocked = changeType === undefined || affectedCount === null || !!busy;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/50" onClick={onCancel} />
      <div className="relative w-full max-w-lg rounded-2xl border border-stone-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-stone-200 px-5 py-3.5">
          <div>
            <h3 className="text-sm font-bold text-stone-900">Publish this deal version</h3>
            <p className="mt-0.5 text-[11.5px] text-stone-500">
              A published version is what merchants sign against. It never changes afterwards.
            </p>
          </div>
          <button onClick={onCancel} className="rounded-lg p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700">
            <X size={16} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
          <div className="mb-4">
            <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-stone-500">What changed</div>
            {changes.length === 0 ? (
              <p className="rounded-lg bg-stone-50 p-3 text-[12px] text-stone-500">
                Nothing differs from the current version.
              </p>
            ) : (
              <div className="space-y-1">
                {changes.map((c) => (
                  <div key={c.path} className="flex flex-wrap items-baseline gap-2 rounded-lg border border-stone-200 px-2.5 py-1.5">
                    <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[9.5px] font-bold uppercase ${IMPACT_CHIP[c.impact]}`}>
                      {c.impact}
                    </span>
                    <span className="text-[12px] font-semibold text-stone-800">{c.label}</span>
                    <span className="font-mono text-[11px] text-stone-500">
                      {c.before ?? '—'} → {c.after ?? '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <fieldset className="mb-4">
            <legend className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-stone-500">
              Is this material?
            </legend>
            <label className="mb-1.5 flex cursor-pointer items-start gap-2.5 rounded-lg border border-stone-200 p-2.5 hover:bg-stone-50">
              <input
                type="radio" name="dealChangeType" aria-label="Material"
                checked={changeType === 'material'}
                onChange={() => onChangeType('material')}
                className="mt-0.5 accent-rose-600"
              />
              <span>
                <span className="block text-[12.5px] font-semibold text-stone-900">Material</span>
                <span className="block text-[11px] text-stone-500">
                  Changes what a merchant agreed to. Everyone on an older version is flagged stale
                  until somebody adopts this one for them.
                </span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-stone-200 p-2.5 hover:bg-stone-50">
              <input
                type="radio" name="dealChangeType" aria-label="Minor"
                checked={changeType === 'minor'}
                onChange={() => onChangeType('minor')}
                className="mt-0.5 accent-stone-600"
              />
              <span>
                <span className="block text-[12.5px] font-semibold text-stone-900">Minor</span>
                <span className="block text-[11px] text-stone-500">
                  Wording or presentation. Makes nobody newly stale.
                </span>
              </span>
            </label>
          </fieldset>

          {onEffectiveAtChange && (
            <div className="mb-4">
              <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-stone-500">
                Effective from
              </label>
              <input
                type="date"
                value={(effectiveAt || '').slice(0, 10)}
                onChange={(e) => onEffectiveAtChange(new Date(`${e.target.value}T00:00:00.000Z`).toISOString())}
                className="rounded-lg border border-stone-300 bg-white px-2.5 py-1.5 text-[12.5px] text-stone-900 [color-scheme:light]"
              />
              <p className="mt-1 text-[10.5px] text-stone-400">
                A future date announces the change before it bites. Until then the current version stays live.
              </p>
            </div>
          )}

          {/* THE BLAST RADIUS, stated honestly for BOTH change types. */}
          <div className="rounded-lg border border-stone-200 bg-stone-50 p-3">
            {affectedCount === null ? (
              <p className="flex items-center gap-1.5 text-[12px] text-stone-500">
                <AlertTriangle size={13} className="text-amber-500" />
                Counting who this affects…
              </p>
            ) : (
              <p className="text-[12px] text-stone-700">
                <b className="font-mono">{affectedCount}</b>
                {' '}signed {affectedCount === 1 ? 'party is' : 'parties are'} on an older version
                {changeType === 'minor' && (
                  <span className="text-stone-500">
                    {' '}— a minor publish makes nobody NEWLY stale, but it does not clear anyone
                    already stale from an earlier material change.
                  </span>
                )}
              </p>
            )}
          </div>

          {error && (
            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-[11.5px] text-red-800">{error}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-stone-200 px-5 py-3">
          <button
            onClick={onCancel}
            className="rounded-lg border border-stone-300 px-4 py-2 text-[12.5px] font-semibold text-stone-700 hover:bg-stone-50"
          >
            Cancel
          </button>
          <button
            onClick={() => { if (!blocked) onConfirm(); }}
            disabled={blocked}
            title={changeType === undefined
              ? 'Choose whether this change is material'
              : affectedCount === null ? 'Still counting who this affects' : undefined}
            className="rounded-lg px-4 py-2 text-[12.5px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
            style={{ background: '#DC143C' }}
          >
            {busy ? 'Publishing…' : 'Publish version'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DealPublishModal;
